import React, { useEffect, useState } from "react";
import { TableBody, TableCell, TableHead, TableSortLabel } from "@mui/material";
import {
	StyledTable,
	TableWrapper,
	TableButton,
	StyledTableRow,
	StyledCheckbox,
	StyledTablePagination,
} from "./DataTable.styles";
import { Link } from "react-router-dom";
import { getFormattedDate } from "../../utils/DateUtils";
import { getNairaFormat, getRequestValue } from "../../utils/Utils";
import { getApprovalStatus, getUserName } from "../../screens/lease/utils";
import { useSelector } from "react-redux";

/* eslint-disable react/prop-types */
const DataTable = ({ TableData, headCells, TableToolbar }) => {
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(8);
	const [selected, setSelected] = useState([]);

	// console.log(TableData, headCells);

	useEffect(() => {
		// Resetting the table data when the TableData prop changes
		setSelected([]);
		setPage(0);
		setOrder("asc");
		setOrderBy("");
	}, [TableData]);

	const getVisibleRows = () => {
		function descendingComparator(a, b, orderBy) {
			if (b[orderBy] < a[orderBy]) {
				return -1;
			}
			if (b[orderBy] > a[orderBy]) {
				return 1;
			}
			return 0;
		}

		function getComparator(order, orderBy) {
			return order === "desc"
				? (a, b) => descendingComparator(a, b, orderBy)
				: (a, b) => -descendingComparator(a, b, orderBy);
		}

		// Check if TableData is not an array || TableData is empty
		if (Array.isArray(TableData)) {
			return TableData.slice().sort(getComparator(order, orderBy));
		} else return [];
	};

	const visibleRows = getVisibleRows().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleClick = (e, id) => {
		const selectedRow = TableData.find((row) => row.objectId === id);
		const selectedIndex = selected.findIndex((row) => row.objectId === selectedRow.objectId);

		if (selectedIndex === -1) {
			setSelected((prevSelected) => [...prevSelected, selectedRow]);
		} else {
			setSelected((prevSelected) => [
				...prevSelected.slice(0, selectedIndex),
				...prevSelected.slice(selectedIndex + 1),
			]);
		}
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			const newSelected = TableData.map((row) => row);
			setSelected(newSelected);
			return;
		}
		setSelected([]);
	};

	const handleChangePage = (e, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (e) => {
		setRowsPerPage(+e.target.value);
		setPage(0);
	};

	return (
		<>
			<TableToolbar selected={selected} />

			<TableWrapper>
				<StyledTable sx={{ minWidth: 650 }}>
					<TableHead>
						<StyledTableRow>
							<TableCell padding='checkbox'>
								<StyledCheckbox
									color='default'
									checked={selected.length === TableData.length && TableData.length !== 0}
									onChange={(e) => handleSelectAll(e)}
									inputProps={{ "aria-labelledby": "Select All" }}
								/>
							</TableCell>

							{headCells.map((headerCell) => (
								<TableCell key={headerCell.field} align='left' sx={{ minWidth: headerCell.width }}>
									<TableSortLabel
										active={orderBy === headerCell.field}
										onClick={() => handleRequestSort(headerCell.field)}
										direction={orderBy === headerCell.field ? order : "asc"}>
										{headerCell.title}
									</TableSortLabel>
								</TableCell>
							))}
						</StyledTableRow>
					</TableHead>

					<DataTableBody
						selected={selected}
						visibleRows={visibleRows}
						handleClick={handleClick}
						headCells={headCells}
					/>
				</StyledTable>
			</TableWrapper>

			<StyledTablePagination
				rowsPerPageOptions={[8, 15, 25, 100]}
				component='div'
				count={TableData.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</>
	);
};

const DataTableBody = ({ selected, visibleRows, handleClick, headCells }) => {
	const { adminQuickRequests } = useSelector((state) => state.lease);

	const { adminQuickAppRequests, investorSponsoredRequests } = useSelector((state) => state.apprenticeship);

	const getNumberOfAsterics = (obj) => {
		// console.log(obj?.usersRequest, obj, obj?.objectId);
		if (obj?.state === "ongoing") {
			return;
		}
		if (adminQuickRequests?.filter((a) => a?.assetObj?.id === obj?.assetObj?.id).length > 0) {
			return `${adminQuickRequests
				.filter((a) => a?.assetObj?.id === obj?.assetObj?.id)
				.fill("*")
				.join("")} `;
		} else if (obj?.assetObj) {
			return "*";
		} else {
			return "";
		}
	};

	const getNumberOfAstericsTwo = (obj) => {
		if (obj.leaseType === "Quick Lease") {
			return;
		}
		if (obj?.state === "ongoing") {
			return;
		}
		if (obj?.assetObj || obj?.assetId) {
			const requestLength = obj?.usersRequest?.length || 0;
			return `${"".padStart(requestLength, "*")}`;
		} else {
			return "";
		}
	};

	const appFinaceQuickAsterics = (obj) => {
		if (obj?.state === "ongoing") {
			return;
		}
		if (obj?.type === "sponsored") {
			return;
		}
		if (
			adminQuickAppRequests?.filter(
				(a) => a?.investorFinanceInfo?.financeId === obj?.investorFinanceInfo?.financeId
			).length > 0
		) {
			return `${adminQuickAppRequests
				.filter((a) => a?.investorFinanceInfo?.financeId === obj?.investorFinanceInfo?.financeId)
				.fill("*")
				.join("")} `;
		} else if (obj?.investorFinanceInfo) {
			return "*";
		} else {
			return "";
		}
	};

	const appFinaceSponsoredAsterics = (obj) => {
		// console.log(obj?.usersRequest, obj, obj?.objectId);
		if (obj?.state === "ongoing") {
			return;
		}
		if (obj?.type === "quick") {
			return;
		}
		if (
			investorSponsoredRequests?.filter(
				(a) => a?.investorFinanceInfo?.financeId === obj?.investorFinanceInfo?.financeId
			).length > 0
		) {
			return `${investorSponsoredRequests
				.filter((a) => a?.investorFinanceInfo?.financeId === obj?.investorFinanceInfo?.financeId)
				.fill("*")
				.join("")} `;
		} else if (obj?.investorFinanceInfo) {
			return "*";
		} else {
			return "";
		}
	};

	const isSelected = (id) => selected.findIndex((row) => row.objectId === id) !== -1;

	const TableRowItem = ({ row, headerCell }) => {
		// console.log(headerCell);
		if (headerCell.title === "ACTION" && headerCell.field === "insurance") {
			return (
				<TableButton
					component={Link}
					variant='outlined'
					size='small'
					to={`/lease/${headerCell.field}/${row.objectId}`}>
					View
				</TableButton>
			);
		}

		if (
			headerCell.title === "ACTION" &&
			headerCell.field === "asset_registration" &&
			row?.productCategory === "Vehicles"
		) {
			return (
				<TableButton
					component={Link}
					variant='outlined'
					size='small'
					to={`/lease/${headerCell.field}/${row.objectId}`}>
					View
				</TableButton>
			);
		}

		if (
			headerCell.title === "ACTION" &&
			headerCell.field === "asset_registration" &&
			row?.productCategory !== "Vehicles"
		) {
			return <p style={{ fontWeight: "bold", textAlign: "center" }}>N/A</p>;
		}

		if (headerCell.title === "ACTION") {
			const hasFilter = headerCell.field.includes("filter");
			const filter = headerCell.field.split("_")[1];

			return (
				<TableButton
					component={Link}
					variant='outlined'
					size='small'
					to={hasFilter ? `./${row.objectId}/${filter}` : `./${row.objectId}`}>
					View
				</TableButton>
			);
		}

		//scheme owner fields

		if (headerCell.field === "viewScheme") {
			return (
				<TableButton component={Link} variant='outlined' size='small' to={`/scheme/${row.objectId}`}>
					View
				</TableButton>
			);
		}

		if (headerCell.field === "approve") {
			const status = row[headerCell.field] ? row[headerCell.field] : false;

			switch (status) {
				case true:
					return <span className='badge approved'>Approved</span>;
				case false:
					return <span className='badge pending'>Pending</span>;
				default:
					return <span className='badge pending'>Pending</span>;
			}
		}

		if (headerCell.field === "createdAt") {
			return <span className={headerCell.field}>{getFormattedDate(row["createdAt"])}</span>;
		}

		if (headerCell.field === "assetObj?.id") {
			return <span className={headerCell.field}>{row?.assetObj?.id || row?.assetId?.objectId || "---"}</span>;
		}

		const dateFields = ["deliveryTime"];
		if (dateFields.includes(headerCell.field)) {
			let fieldValue = row[headerCell.field]?.iso;
			return <span className={headerCell.field}>{getFormattedDate(fieldValue)}</span>;
		}

		const UserNameFields = ["leasee", "investor", "apprentice", "fullName"];
		if (UserNameFields.includes(headerCell.field)) {
			return <span className={headerCell.field}>{getUserName(row)}</span>;
		}

		// Product Fields (row.productId)
		const ProductFields = ["product.productName"];
		if (ProductFields.includes(headerCell.field)) {
			const field = headerCell.field.split(".")[1];
			return <span className={headerCell.field}>{row?.productId ? row.productId[field] : "---"}</span>;
		}

		const InvestorFields = ["investor.fullName"];
		if (InvestorFields.includes(headerCell.field)) {
			const field = headerCell.field.split(".")[1];
			return <span className={headerCell.field}>{row?.investorId ? row.investorId[field] : "---"}</span>;
		}

		const AmountFields = [
			"amount",
			"price",
			"amountPaid",
			"amountDue",
			"amountToSave",
			"amount",
			"amountToRaise",
			"expectedProfit",
			"amountToInvest",
			"amountToRepay",
		];
		if (AmountFields.includes(headerCell.field)) {
			return <span className={headerCell.field}>{getNairaFormat(row[headerCell.field])}</span>;
		}

		if (headerCell.field === "deliveryInfo.deliveryFee") {
			return <span className={headerCell.field}>{getNairaFormat(row?.deliveryInfo?.deliveryFee)}</span>;
		}

		if (headerCell.field === "deliveryInfo.deliveryDate") {
			return (
				<span className={headerCell.field}>
					{new Date(`${row?.deliveryInfo?.deliveryDate}T00:00:00.000Z`).toDateString()}
				</span>
			);
		}

		if (headerCell.field === "paymentStatus") {
			const status = row[headerCell.field] ? row[headerCell.field] : "BAD";

			switch (status) {
				case "SETTLED":
					return <span className='badge success'>{status}</span>;
				case "OVERDUE":
					return <span className='badge overdue'>{status}</span>;
				case "BAD":
					return <span className='badge bad'>{status}</span>;
				default:
					return <span className='badge default'>{status}</span>;
			}
		}

		if (headerCell.field === "request") {
			return <span className={headerCell.field}>{getRequestValue(row)}</span>;
		}

		if (headerCell.field === "trnId") {
			return <span className={headerCell.field}>{"TRN-" + row.objectId}</span>;
		}

		const percentageFields = ["interestRate", "interest"];
		if (percentageFields.includes(headerCell.field)) {
			const hasPercentageSymbol = row[headerCell.field]?.toString().includes("%");
			const value = hasPercentageSymbol ? row[headerCell.field] : row[headerCell.field] + "%";

			return <span className={headerCell.field}>{row[headerCell.field] ? value : "---"}</span>;
		}

		if (headerCell.field === "BusinessInterest") {
			return <span className={headerCell.field}>{row?.businessId?.businessInterest || "---"}</span>;
		}

		const statusFields = [
			"status",
			"approvalStatus",
			"approved",
			"maintenaceStatus",
			"maintenanceStatus",
			"inspectionStatus",
			"retainmentStatus",
			"repairStatus",
			"test",
		];
		if (statusFields.includes(headerCell.field)) {
			const requestStatus = row?.usersRequest?.find((a) => a?.transactionId === row?.objectId);

			const leaseStatus = getApprovalStatus(row, requestStatus);

			const status = row[headerCell.field];
			if (status) {
				return <span className={"badge " + leaseStatus?.toLowerCase()}>{leaseStatus?.toUpperCase()}</span>;
				// return <span className={"badge " + status.toLowerCase()}>{status.toUpperCase()}</span>;
			} else {
				return <span className='badge warning'>N/A</span>;
			}
		}

		// Fields inside the an object (row.user)
		if (headerCell.field.includes(".")) {
			const field = headerCell.field.split(".")[1];
			const obj = row[headerCell.field.split(".")[0]];
			return <span className={headerCell.field}>{obj ? obj[field] : "---"}</span>;
		}

		if (headerCell.title === "LEASE TYPE") {
			return (
				<span className={headerCell.field}>
					{row[headerCell.field]?.includes("Quick") ? (
						<>
							Quick Lease<span style={{ color: "red" }}>{getNumberOfAsterics(row)}</span>
						</>
					) : row[headerCell.field]?.includes("Sponsored") ? (
						<>
							Sponsored Lease<span style={{ color: "blue" }}>{getNumberOfAstericsTwo(row)}</span>
						</>
					) : (
						row[headerCell.field]
					)}
				</span>
			);
		}

		if (headerCell.title === "AF TYPE") {
			return (
				<span className={headerCell.field}>
					{row[headerCell.field] === "quick" ? (
						<>
							Quick Request<span style={{ color: "red" }}>{appFinaceQuickAsterics(row)}</span>
						</>
					) : row[headerCell.field] === "sponsored" ? (
						<>
							Sponsored Request<span style={{ color: "blue" }}>{appFinaceSponsoredAsterics(row)}</span>
						</>
					) : (
						row[headerCell.field]
					)}
				</span>
			);
		}

		if (headerCell.title === "PURPOSE") {
			return <span className='clamp-text'>{row[headerCell.field]}</span>;
		}

		return <span className={headerCell.field}>{row[headerCell.field] ? row[headerCell.field] : "---"}</span>;
	};

	return (
		<TableBody>
			{visibleRows.map((row) => {
				const isItemSelected = isSelected(row.objectId);
				return (
					<StyledTableRow
						hover
						key={row.objectId}
						onClick={(e) => handleClick(e, row.objectId)}
						role='checkbox'
						selected={isItemSelected}>
						<TableCell padding='checkbox'>
							<StyledCheckbox
								color='default'
								checked={isItemSelected}
								inputProps={{ "aria-labelledby": row.objectId }}
							/>
						</TableCell>

						{headCells.map((headerCell) => (
							<TableCell key={headerCell.field} align='left'>
								<TableRowItem row={row} headerCell={headerCell} />
							</TableCell>
						))}
					</StyledTableRow>
				);
			})}

			{visibleRows.length === 0 && (
				<StyledTableRow>
					<TableCell colSpan={headCells.length + 1} align='center' className='no_data_found'>
						NO DATA FOUND
					</TableCell>
				</StyledTableRow>
			)}
		</TableBody>
	);
};

export default DataTable;
